import React from 'react';
import './WalletSelectionModal.css';

const MOBILE_USER_AGENT_REGEX = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
const DESKTOP_ONLY_MESSAGE = 'Wallet connection is only available on the desktop version for now.';

const detectMobileDevice = () => {
    if (typeof navigator !== 'undefined') {
        const userAgent = navigator.userAgent || navigator.vendor || (typeof window !== 'undefined' && window.opera) || '';
        if (MOBILE_USER_AGENT_REGEX.test(userAgent)) {
            return true;
        }
    }

    if (typeof window !== 'undefined') {
        if (typeof window.matchMedia === 'function') {
            try {
                if (window.matchMedia('(max-width: 768px)').matches) {
                    return true;
                }
            } catch (error) {
                console.warn('[WalletSelectionModal] matchMedia check failed:', error);
            }
        }

        if (window.innerWidth && window.innerWidth <= 768) {
            return true;
        }
    }

    return false;
};

/**
 * Native wallet picker. The caller passes the wallet catalog, so the same
 * component can back both a hard-coded list and (in our case) the Stellar
 * Wallets Kit's dynamic `ISupportedWallet[]`.
 *
 * Expected shape for each wallet entry:
 *   { id, name, icon, url?, description?, isAvailable? }
 */
const WalletSelectionModal = ({ isOpen, onClose, onSelectWallet, wallets }) => {
    const isMobile = React.useMemo(() => detectMobileDevice(), []);
    if (!isOpen) return null;

    const list = Array.isArray(wallets) ? wallets : [];
    const isLoading = list.length === 0;

    const handleWalletClick = (e, wallet) => {
        if (e && e.stopPropagation) e.stopPropagation();
        if (e && e.preventDefault) e.preventDefault();
        if (e && e.stopImmediatePropagation) e.stopImmediatePropagation();

        if (isMobile) {
            try {
                if (typeof window !== 'undefined' && typeof window.alert === 'function') {
                    window.alert(DESKTOP_ONLY_MESSAGE);
                }
            } catch (error) {
                console.warn('[WalletSelectionModal] Unable to show desktop-only alert:', error);
            }
            return;
        }

        if (isLoading) return;

        if (wallet.isAvailable === false && wallet.url) {
            try {
                window.open(wallet.url, '_blank', 'noopener,noreferrer');
            } catch (err) {
                console.warn('[WalletSelectionModal] Could not open install url:', err);
            }
            return;
        }

        onClose();
        setTimeout(() => onSelectWallet(wallet), 100);
    };

    return (
        <div className="wallet-selection-overlay" onClick={onClose}>
            <div className="wallet-selection-modal" onClick={(e) => e.stopPropagation()}>
                <div className="wallet-selection-header">
                    <h2>Connect Wallet</h2>
                    <button className="close-button" onClick={onClose}>
                        &times;
                    </button>
                </div>
                <div className="wallet-selection-content">
                    <p className={`wallet-selection-description ${isMobile ? 'desktop-only-message' : ''}`}>
                        {isMobile
                            ? DESKTOP_ONLY_MESSAGE
                            : ''}
                    </p>
                    <div className="wallet-options">
                        {isLoading && (
                            <div className="wallet-option loading" aria-live="polite">
                                <div className="wallet-icon" />
                                <div className="wallet-info">
                                    <div className="wallet-name-row">
                                        <h3>Loading wallets…</h3>
                                    </div>
                                </div>
                            </div>
                        )}
                        {list.map((wallet) => {
                            const available = wallet.isAvailable !== false;
                            const availabilityClass = isMobile
                                ? 'desktop-only'
                                : available
                                    ? 'available'
                                    : 'not-installed';
                            const arrowClass = `wallet-arrow ${!isMobile && available ? 'wallet-arrow-visible' : ''}`;
                            return (
                                <div
                                    key={wallet.id}
                                    className={`wallet-option ${availabilityClass}`}
                                    onClick={(e) => handleWalletClick(e, wallet)}
                                    onMouseDown={(e) => {
                                        if (!isMobile) e.stopPropagation();
                                    }}
                                    style={{ cursor: isMobile ? 'not-allowed' : 'pointer', opacity: isMobile ? 0.65 : 1 }}
                                    role="button"
                                    tabIndex={isMobile ? -1 : 0}
                                    aria-label={`Connect with ${wallet.name}`}
                                >
                                    <div className="wallet-icon">
                                        {wallet.icon ? (
                                            <img src={wallet.icon} alt={wallet.name} />
                                        ) : (
                                            <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#EAEAEA' }} />
                                        )}
                                    </div>
                                    <div className="wallet-info">
                                        <div className="wallet-name-row">
                                            <h3>{wallet.name}</h3>
                                            {!available && <span className="install-badge">Install</span>}
                                        </div>
                                        {wallet.description ? (
                                            <p style={{ margin: '6px 0 0', fontSize: '13px', opacity: 0.85 }}>
                                                {wallet.description}
                                            </p>
                                        ) : null}
                                    </div>
                                    <div className={arrowClass}>→</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WalletSelectionModal;
